import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { usePermission, useAnyPermission } from './usePermission'

interface RouteGuardProps {
  /** Hard-gate permission code required to render children. */
  requiredPermission?: string
  /** OR-list: any one grants access. Ignored when `requiredPermission` is set. */
  anyOf?: string[]
  /** Redirect target on deny. Default `/403`. */
  redirectTo?: string
  children: ReactNode
}

/**
 * Wrap a route element to require a permission. SuperAdmin bypasses all checks.
 *
 * Usage inside routes/index.tsx:
 *   <ProtectedRoute>
 *     <RouteGuard requiredPermission="PERMISSION_GROUPS_VIEW">
 *       <MainLayout>...</MainLayout>
 *     </RouteGuard>
 *   </ProtectedRoute>
 */
export const RouteGuard = ({
  requiredPermission,
  anyOf,
  redirectTo = '/403',
  children,
}: RouteGuardProps) => {
  const location = useLocation()
  const hasSingle = usePermission(requiredPermission ?? '')
  const hasAny = useAnyPermission(anyOf ?? [])

  const allowed = requiredPermission ? hasSingle : hasAny
  if (!allowed) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{
          from: location.pathname,
          required: requiredPermission,
          anyOf,
        }}
      />
    )
  }
  return <>{children}</>
}

export default RouteGuard
